import { useEffect } from 'react';

// Browsers surface a history/autofill dropdown over text fields — the list of
// values previously typed into a field with the same name. Inside the app this
// is pure noise: it leaks values between unrelated fields and covers our own
// results/menus (e.g. it sits on top of the catalogue search). We never want it
// anywhere except the login screen (where saved email/password is genuinely
// useful) — and login renders OUTSIDE <AppShell>, so mounting this hook there
// turns it off everywhere it matters without touching login.
//
// We force `autocomplete="off"` on every text-like input/textarea. The shared
// <Input> already defaults to "off", but plenty of raw <input> elements (and
// inputs rendered later inside modals / async results) don't — a MutationObserver
// keeps those covered as they mount. A field can opt back in with
// `data-allow-autofill` (nothing in-app needs to today).

const SKIP_TYPES = new Set(['hidden', 'checkbox', 'radio', 'file', 'range', 'color', 'submit', 'button']);

function disableEl(el: Element): void {
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  if (el.dataset.allowAutofill !== undefined) return;
  if (el instanceof HTMLInputElement && SKIP_TYPES.has(el.type)) return;
  if (el.getAttribute('autocomplete') !== 'off') el.setAttribute('autocomplete', 'off');
}

function scan(root: ParentNode | Element): void {
  if (root instanceof Element) disableEl(root);
  root.querySelectorAll?.('input, textarea').forEach(disableEl);
}

export function useDisableBrowserAutofill(): void {
  useEffect(() => {
    scan(document.body);
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}
